import React, { useEffect, useState } from "react";
import Modal from "../../../../components/Modal/Modal";
import Button from "../../../../components/Button/Button";
import Input from "../../../../components/Input/Input";

const EMPTY_FORM = { from_date: "", to_date: "", reason: "" };

/**
 * Apply-leave form rendered inside the shared Modal. Validation stays local
 * (required dates, sane date range, non-empty reason) - server-side errors
 * still surface the usual way through handleApiError in the hook.
 */
const ApplyLeaveModal = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  // Reset the form every time the modal is (re)opened so a previous
  // submission's values/errors don't linger into the next one.
  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.from_date) nextErrors.from_date = "From date is required";
    if (!form.to_date) nextErrors.to_date = "To date is required";
    if (form.from_date && form.to_date && form.to_date < form.from_date) {
      nextErrors.to_date = "To date cannot be before the from date";
    }
    if (!form.reason.trim()) nextErrors.reason = "Please share a reason for leave";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const isSaved = await onSubmit({
      from_date: form.from_date,
      to_date: form.to_date,
      reason: form.reason.trim(),
    });
    if (isSaved) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Apply Leave"
      size="sm"
      footer={
        <div className="d-flex gap-2">
          <Button type="button" variant="outline-secondary" className="flex-fill" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" variant="primary" className="flex-fill" onClick={handleSubmit} loading={isSubmitting}>
            Submit
          </Button>
        </div>
      }
    >
      <Input
        label="From Date"
        type="date"
        name="from_date"
        value={form.from_date}
        onChangeHandler={handleChange("from_date")}
        required
        error={errors.from_date}
      />
      <Input
        label="To Date"
        type="date"
        name="to_date"
        value={form.to_date}
        onChangeHandler={handleChange("to_date")}
        required
        error={errors.to_date}
      />
      <div className="form-group mb-3">
        <label htmlFor="leave-reason" className="mb-1 mt-1 text-capitalize fw-semibold">
          Reason<i className="text-danger ms-1">*</i>
        </label>
        <textarea
          id="leave-reason"
          name="reason"
          className={`form-control ${errors.reason ? "is-invalid" : ""}`}
          rows={4}
          placeholder="Briefly describe the reason for leave"
          value={form.reason}
          onChange={handleChange("reason")}
        />
        {errors.reason && <div className="invalid-feedback d-block mt-1">{errors.reason}</div>}
      </div>
    </Modal>
  );
};

export default ApplyLeaveModal;
